<script setup lang="ts">
/**
 * 工作区主操作条：按 pipeline_stage + 角色互斥展示
 * 教案：口播确认后常显「指派」+「完成项目」（可与活跃 Task 并存）；
 *       发起人确认校验任务也会将条目标 completed（双路径）
 * 文档/视频：workspace_ready 且无活跃委托时显「完成项目 / 指派」
 */
import { computed, onMounted, ref, watch } from 'vue'
import { message } from 'ant-design-vue'
import { CheckOutlined, SendOutlined, FormOutlined } from '@ant-design/icons-vue'
import { useI18n } from 'vue-i18n'

import { ProjectType } from '@/api/common-types'
import {
  completeWorkItem,
  getWorkItem,
  type WorkItem,
} from '@/modules/project/api/work-item'
import { submitTask } from '@/modules/task/api/task'
import AssignReviewModal from './AssignReviewModal.vue'

/**
 * Fallback when parent does not pass lectureReady:
 * notes_ready only (not completed-alone — demoted lecture can stay completed stage).
 */
function isLessonLectureConfirmed(item: WorkItem | null): boolean {
  if (!item || item.item_type !== ProjectType.LESSON) return false
  return String(item.pipeline_stage || '') === 'notes_ready'
}

function isWorkItemCompleted(item: WorkItem | null): boolean {
  if (!item) return false
  return (
    String(item.pipeline_stage || '') === 'completed' ||
    String(item.status || '') === 'completed'
  )
}

const props = withDefaults(
  defineProps<{
    workItemId: number
    currentUserId: number
    /**
     * Current lecture readiness from design ui_hints (preferred over pipeline heuristic).
     * Default null disables Vue Boolean cast so omitted prop falls back to notes_ready.
     */
    lectureReady?: boolean | null
  }>(),
  { lectureReady: null },
)

const emit = defineEmits<{
  refreshed: [item: WorkItem]
  completed: []
  assigned: [taskId: number]
  submitted: []
}>()

const { t } = useI18n()
const loading = ref(false)
const workItem = ref<WorkItem | null>(null)
const assignVisible = ref(false)

const stage = computed(() => workItem.value?.pipeline_stage || '')
const isCreator = computed(
  () => workItem.value != null && workItem.value.creator_id === props.currentUserId,
)
const currentTask = computed(() => workItem.value?.current_task ?? null)
const isAssignee = computed(
  () =>
    currentTask.value != null &&
    currentTask.value.assignee_id === props.currentUserId &&
    currentTask.value.assignee_id !== currentTask.value.creator_id,
)

const isLesson = computed(() => workItem.value?.item_type === ProjectType.LESSON)
const lectureConfirmed = computed(() => {
  if (typeof props.lectureReady === 'boolean') return props.lectureReady
  return isLessonLectureConfirmed(workItem.value)
})
/** 教案：当前讲稿已确认即可；文档/视频：workspace_ready 且无活跃委托 */
const showCompleteProject = computed(() => {
  if (!isCreator.value || isWorkItemCompleted(workItem.value)) return false
  if (isLesson.value) return lectureConfirmed.value
  const activeCount = workItem.value?.active_review_summary?.count ?? 0
  const hasActive =
    activeCount > 0 ||
    (currentTask.value != null &&
      ['pending', 'processing', 'submitted'].includes(String(currentTask.value.status)))
  return stage.value === 'workspace_ready' && !hasActive
})
/** 教案：当前讲稿确认后常显指派（可有 Task / WI 已 completed） */
const showAssignReview = computed(() => {
  if (!isCreator.value) return false
  if (isLesson.value) return lectureConfirmed.value
  const activeCount = workItem.value?.active_review_summary?.count ?? 0
  const hasActive =
    activeCount > 0 ||
    (currentTask.value != null &&
      ['pending', 'processing', 'submitted'].includes(String(currentTask.value.status)))
  return stage.value === 'workspace_ready' && !hasActive
})
const showSubmitTask = computed(() => {
  if (!isAssignee.value || String(currentTask.value?.status) !== 'processing') return false
  // 制品态不再写 in_review；有 PROCESSING 委托即可提交
  return true
})

async function refresh() {
  if (!props.workItemId) return
  workItem.value = await getWorkItem(props.workItemId)
  emit('refreshed', workItem.value)
}

onMounted(refresh)
watch(() => props.workItemId, refresh)

async function onCompleteProject() {
  loading.value = true
  try {
    await completeWorkItem(props.workItemId)
    message.success(t('project.actionBar.completeProjectSuccess'))
    await refresh()
    emit('completed')
  } catch (e) {
    console.error(e)
    /* apiClient already toasted */
  } finally {
    loading.value = false
  }
}

function openAssign() {
  if (isLesson.value) {
    if (!lectureConfirmed.value) {
      message.warning(t('project.actionBar.assignLectureNotReady'))
      return
    }
    assignVisible.value = true
    return
  }
  if (stage.value !== 'workspace_ready') {
    message.warning(t('project.actionBar.assignNotReady'))
    return
  }
  assignVisible.value = true
}

async function onAssigned(taskId: number) {
  assignVisible.value = false
  message.success(t('project.actionBar.assignSuccess'))
  await refresh()
  emit('assigned', taskId)
  // 教案指派成功后留在当前页（机翻异步建 Task）；不跳旧 translation / workspace
}

async function onSubmitTask() {
  const taskId = currentTask.value?.id
  if (!taskId) return
  loading.value = true
  try {
    await submitTask(taskId)
    message.success(t('project.actionBar.submitSuccess'))
    await refresh()
    emit('submitted')
  } catch (e) {
    console.error(e)
    /* apiClient already toasted */
  } finally {
    loading.value = false
  }
}

defineExpose({ refresh })
</script>

<template>
  <a-space>
    <a-button
      v-if="showCompleteProject"
      type="primary"
      :loading="loading"
      @click="onCompleteProject"
    >
      <template #icon><CheckOutlined /></template>
      {{ t('project.actionBar.completeProject') }}
    </a-button>
    <a-button
      v-if="showAssignReview"
      :loading="loading"
      @click="openAssign"
    >
      <template #icon><SendOutlined /></template>
      {{ t('project.actionBar.assignReview') }}
    </a-button>
    <a-button
      v-if="showSubmitTask"
      type="primary"
      :loading="loading"
      @click="onSubmitTask"
    >
      <template #icon><FormOutlined /></template>
      {{ t('project.actionBar.submitTask') }}
    </a-button>

    <AssignReviewModal
      v-model:visible="assignVisible"
      :work-item-id="workItemId"
      :item-type="workItem?.item_type"
      @assigned="onAssigned"
    />
  </a-space>
</template>
